/**
 * Product Card Quantity Selector Controller
 * Manages inline quantity selectors on product cards, syncing seamlessly with
 * Shopify cart (/cart/add.js, /cart/change.js, /cart.js), external cart apps (Yuko Quick Cart / Cart Drawer),
 * the floating bottom cart toast, and header bubble.
 */

(function () {
  'use strict';

  // In-flight request guards per variant to prevent race conditions
  const pendingRequests = new Set();

  // Pattern matching Shopify cart modify URLs
  const CART_MODIFY_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  // Debounce and queue guards for fetching cart
  let fetchCartTimer = null;
  let isFetchingCart = false;

  /**
   * Helper: Check if a URL is a cart modification endpoint
   */
  function isCartModifyUrl(url) {
    if (!url || typeof url !== 'string') return false;
    return CART_MODIFY_RE.test(url);
  }

  /**
   * Helper: Check if request was initiated by this controller to prevent loops
   */
  function isSelfRequest(url, initOrHeaders) {
    if (typeof url === 'string' && url.includes('source=card-quantity-selector')) {
      return true;
    }
    if (initOrHeaders) {
      const headers = initOrHeaders.headers || initOrHeaders;
      if (headers) {
        if (typeof headers.get === 'function') {
          if (headers.get('X-Requested-With') === 'card-quantity-selector') return true;
        } else if (headers['X-Requested-With'] === 'card-quantity-selector') {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Helper: Extract cart object with items array from any event or payload
   */
  function extractCartData(obj) {
    if (!obj) return null;
    if (Array.isArray(obj.items)) return obj;
    if (obj.cart && Array.isArray(obj.cart.items)) return obj.cart;
    if (obj.resource && Array.isArray(obj.resource.items)) return obj.resource;
    if (obj.detail) return extractCartData(obj.detail);
    return null;
  }

  /**
   * Helper: Gather section IDs for cart drawer morphing
   */
  function getCartSectionIds() {
    const sections = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) {
        sections.push(el.dataset.sectionId);
      }
    });
    return sections;
  }

  /**
   * Helper: Extract product image and title from a card
   */
  function getCardProductInfo(card) {
    const img = card.querySelector('.product-image img') ||
                card.querySelector('.resource-image img') ||
                card.querySelector('img');
    const title = card.querySelector('.product-title a')?.textContent?.trim() ||
                  card.querySelector('h3.product-title')?.textContent?.trim() ||
                  card.querySelector('.product-title')?.textContent?.trim() ||
                  card.querySelector('h1')?.textContent?.trim() ||
                  img?.alt ||
                  'Product';
    return {
      image: img?.src || '',
      title: title
    };
  }

  /**
   * Helper: Get variant ID from card
   */
  function getCardVariantId(card) {
    const controls = card.querySelector('.card-qty-controls');
    if (controls && controls.dataset.variantId) return controls.dataset.variantId.toString();

    const input = card.querySelector('input[name="id"]');
    if (input && input.value) return input.value.toString();

    const select = card.querySelector('select[name="id"]');
    if (select && select.value) return select.value.toString();

    return card.dataset.variantId || card.dataset.productId || '';
  }

  /**
   * Helper: Ensure controls exist in card
   */
  function ensureCardControls(card, variantId) {
    let controls = card.querySelector('.card-qty-controls');
    if (controls) {
      if (variantId && !controls.dataset.variantId) {
        controls.dataset.variantId = variantId;
      }
      return controls;
    }

    const btn = card.querySelector('button[name="add"], .buy-btn, .product-btn, .add-to-cart-button');
    if (!btn) return null;

    controls = document.createElement('div');
    controls.className = 'card-qty-controls';
    if (variantId) controls.dataset.variantId = variantId;
    controls.style.display = 'none';
    controls.innerHTML = `
      <button type="button" class="card-qty-btn card-qty-btn--minus card-qty-btn--trash" aria-label="Remove from cart">
        <span class="card-qty-icon--trash" style="display: inline-flex;">
          <svg width="18" height="20" viewBox="0 0 18 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 5h14"/>
            <path d="M6.5 5V2.8a0.8 0.8 0 0 1 0.8-0.8h3.4a0.8 0.8 0 0 1 0.8 0.8V5"/>
            <path d="M3.5 5l0.9 11.5a1.5 1.5 0 0 0 1.5 1.5h6.2a1.5 1.5 0 0 0 1.5-1.5L14.5 5"/>
            <line x1="6.8" y1="8.5" x2="6.8" y2="15.5"/>
            <line x1="9" y1="8.5" x2="9" y2="15.5"/>
            <line x1="11.2" y1="8.5" x2="11.2" y2="15.5"/>
          </svg>
        </span>
        <span class="card-qty-icon--minus" style="display: none;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </span>
      </button>
      <span class="card-qty-count">1</span>
      <button type="button" class="card-qty-btn card-qty-btn--plus" aria-label="Increase quantity">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </button>
    `;

    btn.parentNode.insertBefore(controls, btn.nextSibling);
    return controls;
  }

  /**
   * Helper: Apply quantity state to a single card
   */
  function applyCardQuantity(card, quantity) {
    const variantId = getCardVariantId(card);
    const controls = ensureCardControls(card, variantId);
    const btn = card.querySelector('button[name="add"], .buy-btn, .product-btn, .add-to-cart-button');

    if (quantity > 0) {
      if (btn) btn.style.display = 'none';
      if (controls) {
        controls.classList.add('is-active');
        controls.style.display = 'flex';
        const count = controls.querySelector('.card-qty-count');
        if (count) count.textContent = String(quantity);

        const minusBtn = controls.querySelector('.card-qty-btn--minus');
        const trashIcon = controls.querySelector('.card-qty-icon--trash');
        const minusIcon = controls.querySelector('.card-qty-icon--minus');

        if (quantity === 1) {
          if (trashIcon) trashIcon.style.display = 'inline-flex';
          if (minusIcon) minusIcon.style.display = 'none';
          if (minusBtn) {
            minusBtn.classList.add('card-qty-btn--trash');
            minusBtn.setAttribute('aria-label', 'Remove from cart');
          }
        } else {
          if (trashIcon) trashIcon.style.display = 'none';
          if (minusIcon) minusIcon.style.display = 'inline-flex';
          if (minusBtn) {
            minusBtn.classList.remove('card-qty-btn--trash');
            minusBtn.setAttribute('aria-label', 'Decrease quantity');
          }
        }
      }
      card.setAttribute('data-has-qty', 'true');
    } else {
      if (controls) {
        controls.classList.remove('is-active');
        controls.style.display = 'none';
        const count = controls.querySelector('.card-qty-count');
        if (count) count.textContent = '1';
        const trashIcon = controls.querySelector('.card-qty-icon--trash');
        const minusIcon = controls.querySelector('.card-qty-icon--minus');
        if (trashIcon) trashIcon.style.display = 'inline-flex';
        if (minusIcon) minusIcon.style.display = 'none';
      }
      if (btn) btn.style.display = '';
      card.removeAttribute('data-has-qty');
    }
  }

  /**
   * Helper: Sync all cards on the page for a specific variant
   */
  function syncVariantQuantityAcrossCards(variantId, quantity) {
    if (!variantId) return;
    const targetVid = variantId.toString();

    // 1. Check all elements with .card-qty-controls directly
    const processedCards = new Set();
    document.querySelectorAll('.card-qty-controls').forEach((controls) => {
      const card = controls.closest('.product-card, product-card, add-to-cart-component, .product-form') || controls.parentElement;
      if (!card || card.closest('.product-details') || card.closest('.product-information')) return;

      const cardVid = controls.dataset.variantId || getCardVariantId(card);
      if (cardVid && cardVid.toString() === targetVid) {
        processedCards.add(card);
        applyCardQuantity(card, quantity);
      }
    });

    // 2. Check all card containers
    const cards = document.querySelectorAll('.product-card, product-card, add-to-cart-component');
    cards.forEach((card) => {
      if (processedCards.has(card)) return;
      if (card.closest('.product-details') || card.closest('.product-information')) return;

      const cardVarId = getCardVariantId(card);
      if (cardVarId && cardVarId.toString() === targetVid) {
        applyCardQuantity(card, quantity);
      }
    });
  }

  /**
   * Helper: Broadcast cart:update event for theme components (drawer, header icon)
   */
  function broadcastCartUpdate(cartData, sections) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cartData,
        sourceId: 'card-quantity-selector',
        data: {
          source: 'card-quantity-selector',
          itemCount: cartData.item_count,
          sections: sections || cartData.sections || {}
        }
      }
    }));
  }

  /**
   * Handler: Add to cart (switches button to quantity selector with 1)
   */
  async function handleAddToCart(card, button) {
    const variantId = getCardVariantId(card);
    if (!variantId || pendingRequests.has(variantId)) return;

    pendingRequests.add(variantId);
    const info = getCardProductInfo(card);

    // 1. Optimistic UI update — instant feedback
    syncVariantQuantityAcrossCards(variantId, 1);

    // 2. Trigger toaster — pops up instantly
    if (typeof window.showCartToast === 'function') {
      window.showCartToast({ action: 'add', image: info.image, title: info.title });
    }

    const controls = card.querySelector('.card-qty-controls');
    if (controls) controls.classList.add('is-loading');

    // 3. Post to /cart/add.js
    const sectionIds = getCartSectionIds();
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    try {
      const res = await fetch('/cart/add.js?source=card-quantity-selector', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'card-quantity-selector'
        },
        body: formData
      });

      if (!res.ok) {
        throw new Error('Failed to add to cart');
      }

      const addResponse = await res.json();

      // Fetch fresh cart to get actual total item count
      const cartRes = await fetch('/cart.js');
      const cartData = await cartRes.json();

      broadcastCartUpdate(cartData, addResponse.sections || {});
    } catch (err) {
      console.error('Error adding product to cart:', err);
      // Revert if failed
      syncVariantQuantityAcrossCards(variantId, 0);
    } finally {
      pendingRequests.delete(variantId);
      if (controls) controls.classList.remove('is-loading');
    }
  }

  /**
   * Handler: Change quantity (+ or -)
   */
  async function handleQuantityChange(card, variantId, newQuantity) {
    if (!variantId || pendingRequests.has(variantId)) return;

    pendingRequests.add(variantId);
    const info = getCardProductInfo(card);
    const isRemove = newQuantity <= 0;

    // 1. Optimistic UI update — instant feedback
    syncVariantQuantityAcrossCards(variantId, newQuantity);

    // 2. Trigger toaster — pops up instantly
    if (typeof window.showCartToast === 'function') {
      window.showCartToast({
        action: isRemove ? 'remove' : 'update',
        image: info.image,
        title: info.title
      });
    }

    const controls = card.querySelector('.card-qty-controls');
    if (controls) controls.classList.add('is-loading');

    // 3. Post to /cart/change.js
    const sectionIds = getCartSectionIds();
    const payload = {
      id: variantId,
      quantity: newQuantity
    };
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
    }

    try {
      const res = await fetch('/cart/change.js?source=card-quantity-selector', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'card-quantity-selector'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error('Failed to change quantity');
      }

      const cartData = await res.json();
      broadcastCartUpdate(cartData, cartData.sections || {});
    } catch (err) {
      console.error('Error changing cart quantity:', err);
      // Re-sync with actual cart on error
      fetchCartAndSync();
    } finally {
      pendingRequests.delete(variantId);
      if (controls) controls.classList.remove('is-loading');
    }
  }

  /**
   * Fetch current cart and sync all cards on page
   */
  async function fetchCartAndSync() {
    if (isFetchingCart) {
      if (!fetchCartTimer) {
        fetchCartTimer = setTimeout(() => {
          fetchCartTimer = null;
          fetchCartAndSync();
        }, 250);
      }
      return;
    }

    isFetchingCart = true;
    try {
      const res = await fetch('/cart.js');
      if (res.ok) {
        const cart = await res.json();
        syncAllCardsWithCart(cart);
      }
    } catch (_) {
    } finally {
      isFetchingCart = false;
    }
  }

  /**
   * Sync all cards given a cart object
   */
  function syncAllCardsWithCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;

    // Build variant quantity map
    const variantQtyMap = {};
    cart.items.forEach((item) => {
      const vid = item.variant_id.toString();
      variantQtyMap[vid] = (variantQtyMap[vid] || 0) + item.quantity;
    });

    const processedCards = new Set();

    // 1. Process all cards with .card-qty-controls directly
    const controlsList = document.querySelectorAll('.card-qty-controls');
    controlsList.forEach((controls) => {
      const card = controls.closest('.product-card, product-card, add-to-cart-component, .product-form') || controls.parentElement;
      if (!card || card.closest('.product-details') || card.closest('.product-information')) return;

      const vid = controls.dataset.variantId || getCardVariantId(card);
      if (vid) {
        processedCards.add(card);
        const qty = variantQtyMap[vid.toString()] || 0;
        applyCardQuantity(card, qty);
      }
    });

    // 2. Process all other cards on the page that might not have controls yet
    const cards = document.querySelectorAll('.product-card, product-card, add-to-cart-component');
    cards.forEach((card) => {
      if (processedCards.has(card)) return;
      if (card.closest('.product-details') || card.closest('.product-information')) return;

      const vid = getCardVariantId(card);
      if (vid) {
        const qty = variantQtyMap[vid.toString()] || 0;
        applyCardQuantity(card, qty);
      }
    });
  }

  /**
   * Global Event Delegation for Clicks
   */
  function setupEventListeners() {
    // Add to Cart Button Click
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('button[name="add"], .buy-btn, .product-btn, .add-to-cart-button');
      if (!btn) return;

      const card = btn.closest('.product-card') || btn.closest('product-card') || btn.closest('add-to-cart-component');
      if (!card) return;

      // Ensure it's not a PDP main product button
      if (card.closest('.product-details') || card.closest('.product-information')) return;

      e.preventDefault();
      e.stopPropagation();
      handleAddToCart(card, btn);
    }, true);

    // Quantity Plus Click
    document.addEventListener('click', (e) => {
      const plusBtn = e.target.closest('.card-qty-btn--plus');
      if (!plusBtn) return;

      const card = plusBtn.closest('.product-card') || plusBtn.closest('product-card') || plusBtn.closest('add-to-cart-component');
      if (!card) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = getCardVariantId(card);
      const countEl = card.querySelector('.card-qty-count');
      const currentQty = parseInt(countEl?.textContent || '1', 10);
      handleQuantityChange(card, variantId, currentQty + 1);
    }, true);

    // Quantity Minus Click
    document.addEventListener('click', (e) => {
      const minusBtn = e.target.closest('.card-qty-btn--minus');
      if (!minusBtn) return;

      const card = minusBtn.closest('.product-card') || minusBtn.closest('product-card') || minusBtn.closest('add-to-cart-component');
      if (!card) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = getCardVariantId(card);
      const countEl = card.querySelector('.card-qty-count');
      const currentQty = parseInt(countEl?.textContent || '1', 10);
      handleQuantityChange(card, variantId, Math.max(0, currentQty - 1));
    }, true);

    /**
     * Unified listener for external cart updates:
     * - 'cart:updated' (Yuko Quick Cart / Cart Drawer)
     * - 'cart:refresh' (Yuko & app refreshes)
     * - 'ajaxProduct:added' (Yuko quick cart upsell add)
     * - 'cart:update' (Shopify Theme standard)
     * - 'cart:change', 'cart:clear', 'cart-drawer:updated'
     */
    function handleExternalCartEvent(e) {
      if (e.detail?.sourceId === 'card-quantity-selector' || e.detail?.data?.source === 'card-quantity-selector') {
        return;
      }

      const cartData = extractCartData(e.detail) || extractCartData(e);
      if (cartData && Array.isArray(cartData.items)) {
        syncAllCardsWithCart(cartData);
      } else {
        fetchCartAndSync();
      }
    }

    const CART_EVENTS = [
      'cart:updated',
      'cart:update',
      'cart:refresh',
      'ajaxProduct:added',
      'cart:change',
      'cart:clear',
      'cart-drawer:updated'
    ];

    CART_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, handleExternalCartEvent);
      window.addEventListener(eventName, handleExternalCartEvent);
    });

    // Re-sync on page restoration (back-forward navigation)
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        fetchCartAndSync();
      }
    });

    // Re-sync when switching back to this tab
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        fetchCartAndSync();
      }
    });

    // Re-sync on theme customizer section load
    document.addEventListener('shopify:section:load', () => {
      fetchCartAndSync();
      observeYukoDrawer();
    });

    // Intercept native fetch calls for /cart/*
    installFetchHook();

    // Intercept native XMLHttpRequest calls for /cart/*
    installXhrHook();

    // Hook into YukoNetHooks if available
    subscribeToYukoNetHooks();
    document.addEventListener('yuko:cart:root:registered', () => {
      subscribeToYukoNetHooks();
      fetchCartAndSync();
    });

    // Watch Yuko drawer open/close state
    observeYukoDrawer();
  }

  /**
   * Network Interceptor: Window.fetch hook for /cart/*
   */
  function installFetchHook() {
    if (window.__cardQtyFetchHookInstalled) return;
    window.__cardQtyFetchHookInstalled = true;

    const originalFetch = window.fetch;
    if (typeof originalFetch !== 'function') return;

    window.fetch = async function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url ? args[0].url : '');
      const response = await originalFetch.apply(this, args);

      if (isCartModifyUrl(url) && !isSelfRequest(url, args[1])) {
        if (response && response.ok) {
          try {
            const clone = response.clone();
            clone.json().then((json) => {
              const cart = extractCartData(json);
              if (cart && Array.isArray(cart.items)) {
                syncAllCardsWithCart(cart);
              } else {
                fetchCartAndSync();
              }
            }).catch(() => {
              fetchCartAndSync();
            });
          } catch (_) {
            fetchCartAndSync();
          }
        }
      }

      return response;
    };
  }

  /**
   * Network Interceptor: XMLHttpRequest hook for /cart/*
   */
  function installXhrHook() {
    if (window.__cardQtyXhrHookInstalled) return;
    window.__cardQtyXhrHookInstalled = true;

    if (!window.XMLHttpRequest || !XMLHttpRequest.prototype) return;
    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._cardQtyUrl = typeof url === 'string' ? url : String(url || '');
      return origOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (this._cardQtyUrl && isCartModifyUrl(this._cardQtyUrl) && !isSelfRequest(this._cardQtyUrl)) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) {
            try {
              const json = JSON.parse(this.responseText);
              const cart = extractCartData(json);
              if (cart && Array.isArray(cart.items)) {
                syncAllCardsWithCart(cart);
              } else {
                fetchCartAndSync();
              }
            } catch (_) {
              fetchCartAndSync();
            }
          }
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  /**
   * Integration with YukoNetHooks
   */
  function subscribeToYukoNetHooks() {
    if (window.YukoNetHooks && typeof window.YukoNetHooks.subscribe === 'function') {
      try {
        window.YukoNetHooks.subscribe((evt) => {
          if (!evt || evt.failed) return;
          if (isSelfRequest(evt.url)) return;

          if (evt.response) {
            try {
              evt.response.clone().json().then((json) => {
                const cart = extractCartData(json);
                if (cart && Array.isArray(cart.items)) {
                  syncAllCardsWithCart(cart);
                } else {
                  fetchCartAndSync();
                }
              }).catch(() => {
                fetchCartAndSync();
              });
            } catch (_) {
              fetchCartAndSync();
            }
          } else {
            fetchCartAndSync();
          }
        });
      } catch (_) {}
    }
  }

  /**
   * Watch Yuko drawer open/close
   */
  function observeYukoDrawer() {
    const drawerEl = document.querySelector('#yuko-cart-drawer');
    if (drawerEl && !drawerEl.__cardQtyObserverAttached) {
      drawerEl.__cardQtyObserverAttached = true;
      let wasOpen = drawerEl.classList.contains('is-open');

      const obs = new MutationObserver(() => {
        const isOpen = drawerEl.classList.contains('is-open');
        if (wasOpen && !isOpen) {
          // Drawer was just closed
          if (window.yukoCartDrawer?.cart && Array.isArray(window.yukoCartDrawer.cart.items)) {
            syncAllCardsWithCart(window.yukoCartDrawer.cart);
          } else {
            fetchCartAndSync();
          }
        }
        wasOpen = isOpen;
      });

      obs.observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setupEventListeners();
      fetchCartAndSync();
    });
  } else {
    setupEventListeners();
    fetchCartAndSync();
  }

  // Export for manual or test triggering
  window.syncProductCardQuantities = fetchCartAndSync;
})();
